import { useEffect, useState, useRef, useCallback } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation
} from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Box } from '@mui/material';

import './styles/theme/global.css';

import ROUTES from './config/routes';
import {
  ErrorBoundary,
  RiskGate,
  PageTransition,
  Layout,
  ScrollToTop,
  CookieConsent
} from './components';
import { NavigationProvider, useNavigation } from './context/NavigationContext';
import { scrollToTarget } from './utils/smoothScroll';
// Pages
import HomePage from './pages/home/HomePage';
import { AuthPage } from './auth';
import AboutUsPage from './pages/about-us/AboutUsPage';
import CSRPage from './pages/about-us/CSRPage';
import StrategiesPage from './pages/strategies/StrategiesPage';
import InvestmentProgramsPage from './pages/strategies/InvestmentProgramsPage';
import TradingBusinessVisionPage from './pages/strategies/TradingBusinessPage';
import StrandQPage from './pages/strategies/StrandQPage';
import MerinPage from './pages/strategies/MerinPage';
import DravonPage from './pages/strategies/DravonPage';
import GlobalMacroPage from './pages/strategies/GlobalMacroPage';
import AMCPage from './pages/strategies/AMCPage';
import HowToJoinPage from './pages/how-to-join/HowToJoinPage';
import InternshipPage from './pages/how-to-join/InternshipPage';
import InterviewPage from './pages/how-to-join/InterviewPage';
import PreparingInterviewPage from './pages/how-to-join/PreparingInterviewPage';
import UKRecruitmentPolicyPage from './pages/how-to-join/UKRecruitmentPolicyPage';
import EURecruitmentPolicyPage from './pages/how-to-join/EURecruitmentPolicyPage';
import JobDetailsPage from './pages/how-to-join/JobDetailsPage';
import LibraryPage from './pages/library/LibraryPage';
import BlogPage from './pages/blog/BlogPage';
import BlogPostPage from './pages/blog/BlogPostPage';
import ContactPage from './pages/contact/ContactPage';
import LegalPage from './pages/legal/LegalPage';
import CookiePolicyPage from './pages/legal/CookiePolicyPage';
import ImportantDisclosuresPage from './pages/legal/ImportantDisclosuresPage';
import TaxStrategyPage from './pages/legal/TaxStrategyPage';
import ModernSlaveryPage from './pages/legal/ModernSlaveryPage';
import TermsAndConditionsPage from './pages/legal/TermsAndConditionsPage';
import RecruitmentPolicyPage from './pages/legal/RecruitmentPolicyPage';
import OnlinePrivacyStatementPage from './pages/legal/OnlinePrivacyStatementPage';
import SitemapPage from './pages/SitemapPage';
import NotFoundPage from './pages/NotFoundPage';

/**
 * Wrapper component for routes to ensure proper key management
 * @param {Object} props - Component props
 * @param {React.Component} props.component - Component to render
 * @param {...Object} rest - Additional props to pass to component
 * @returns {React.Element} Wrapped component with route key
 */
const RouteWrapper = ({ component: Component, ...rest }) => {
  const location = useLocation();
  return (
    <Component
      key={location.key || location.pathname + location.search}
      {...rest}
    />
  );
};

/**
 * Main application routes component with page transitions and navigation handling
 * @returns {React.Element} Routes with transition handling
 */
const AppRoutes = () => {
  const location = useLocation();

  const {
    isPageTransitioning,
    endPageTransition,
    pendingNavigationAction,
    setPendingNavigationAction
  } = useNavigation();

  const [displayLocation, setDisplayLocation] = useState(location);
  const [isAnimating, setIsAnimating] = useState(false);
  const pageSectionRefs = useRef({});
  const transitionDuration = 0.9;

  useEffect(() => {
    if (isPageTransitioning) {
      setIsAnimating(true);
    }
  }, [isPageTransitioning]);

  useEffect(() => {
    if (!isAnimating) {
      if (displayLocation.key !== location.key) {
        setDisplayLocation(location);
      }
    }
  }, [location, isAnimating, displayLocation.key]);

  /**
   * Handles completion of page transition animation
   */
  const handleAnimationComplete = useCallback(() => {
    if (pendingNavigationAction) {
      pendingNavigationAction();
      setPendingNavigationAction(null);
    }
    setIsAnimating(false);
    endPageTransition();

    const currentLoc = location;
    if (currentLoc.hash) {
      const id = currentLoc.hash.substring(1);

      setTimeout(() => {
        const element =
          pageSectionRefs.current[id]?.current || document.getElementById(id);
        if (element) {
          scrollToTarget(element);
        } else {
          scrollToTarget(id);
        }

        // Clean up state flag if present
        if (currentLoc.state?.scrollHandledByNav) {
          const { scrollHandledByNav, ...stateWithoutFlag } =
            currentLoc.state || {};
          window.history.replaceState(
            stateWithoutFlag,
            '',
            currentLoc.pathname + currentLoc.search + currentLoc.hash
          );
        }
      }, 50);
    }
  }, [
    pendingNavigationAction,
    location,
    setPendingNavigationAction,
    endPageTransition,
    pageSectionRefs
  ]);

  useEffect(() => {
    const currentLoc = location;
    if (
      !isAnimating &&
      currentLoc.hash &&
      !currentLoc.state?.scrollHandledByNav
    ) {
      const timerId = setTimeout(() => {
        const id = currentLoc.hash.substring(1);
        const element =
          pageSectionRefs.current[id]?.current || document.getElementById(id);
        if (element) {
          scrollToTarget(element);
        } else {
          scrollToTarget(id);
        }
      }, 100);

      return () => clearTimeout(timerId);
    } else if (currentLoc.state?.scrollHandledByNav) {
      setTimeout(() => {
        const currentState = window.history.state || {};
        if (currentState.scrollHandledByNav) {
          const { scrollHandledByNav, timestamp, ...cleanState } = currentState;
          window.history.replaceState(
            Object.keys(cleanState).length > 0 ? cleanState : null,
            '',
            window.location.pathname +
              window.location.search +
              window.location.hash
          );
        }
      }, 500);
    }
  }, [location, isAnimating, pageSectionRefs]);

  /**
   * Callback to set page section refs for the active page
   * @param {Object} refsFromPage - Refs object from the active page component
   */
  const handleSetRefs = useCallback(refsFromPage => {
    pageSectionRefs.current = refsFromPage;
  }, []);

  /**
   * Determines whether the current route should use the main layout
   * @returns {boolean} True if layout should be used
   */
  const shouldUseLayout = () => {
    const path = location.pathname;
    return !(
      path === ROUTES.HOME ||
      path === ROUTES.AUTH ||
      path === ROUTES.CONTACT
    );
  };

  const locationForRoutes = isAnimating ? displayLocation : location;

  return (
    <>
      <PageTransition
        isAnimating={isAnimating}
        onAnimationComplete={handleAnimationComplete}
        duration={transitionDuration}
      />

      {/* Main content wrapper - risk banner overlays, no margin needed */}
      <Box
        sx={{
          minHeight: '100vh'
        }}
      >
        {!shouldUseLayout() && (
          <Routes location={locationForRoutes}>
            <Route
              path={ROUTES.HOME}
              element={<HomePage setRefs={handleSetRefs} />}
            />
            <Route
              path={ROUTES.AUTH}
              element={
                <GoogleReCaptchaProvider
                  reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  scriptProps={{
                    async: false,
                    defer: false,
                    appendTo: 'head'
                  }}
                >
                  <AuthPage />
                </GoogleReCaptchaProvider>
              }
            />
            <Route path={ROUTES.CONTACT} element={<ContactPage />} />
            {/* Catch-all route for 404 */}
            <Route path='*' element={<NotFoundPage />} />
          </Routes>
        )}

        {shouldUseLayout() && (
          <Layout pageSectionRefsContainer={pageSectionRefs}>
            <Routes location={locationForRoutes}>
              <Route
                path={ROUTES.ABOUT_US.ROOT}
                element={<AboutUsPage setRefs={handleSetRefs} />}
              />
              <Route
                path={ROUTES.ABOUT_US.CSR}
                element={<CSRPage setRefs={handleSetRefs} />}
              />
              <Route
                path={ROUTES.STRATEGIES.ROOT}
                element={<StrategiesPage setRefs={handleSetRefs} />}
              />
              <Route
                path={ROUTES.STRATEGIES.INVESTMENT_PROGRAMS}
                element={
                  <RouteWrapper
                    component={InvestmentProgramsPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={'/strategies/amc'}
                element={
                  <RouteWrapper component={AMCPage} setRefs={handleSetRefs} />
                }
              />
              <Route
                path={ROUTES.STRATEGIES.TRADING_BUSINESS}
                element={
                  <RouteWrapper
                    component={TradingBusinessVisionPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.STRATEGIES.STRAND_Q}
                element={
                  <RouteWrapper
                    component={StrandQPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.STRATEGIES.MERIN}
                element={
                  <RouteWrapper component={MerinPage} setRefs={handleSetRefs} />
                }
              />
              <Route
                path={ROUTES.STRATEGIES.DRAVON}
                element={
                  <RouteWrapper
                    component={DravonPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.STRATEGIES.GLOBAL_MACRO}
                element={
                  <RouteWrapper
                    component={GlobalMacroPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.HOW_TO_JOIN.ROOT}
                element={<HowToJoinPage setRefs={handleSetRefs} />}
              />
              <Route
                path={ROUTES.HOW_TO_JOIN.INTERNSHIP}
                element={
                  <RouteWrapper
                    component={InternshipPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.HOW_TO_JOIN.INTERVIEW}
                element={
                  <RouteWrapper
                    component={InterviewPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.HOW_TO_JOIN.PREPARING_INTERVIEW}
                element={
                  <RouteWrapper
                    component={PreparingInterviewPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.HOW_TO_JOIN.UK_RECRUITMENT}
                element={
                  <RouteWrapper
                    component={UKRecruitmentPolicyPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.HOW_TO_JOIN.EU_RECRUITMENT}
                element={
                  <RouteWrapper
                    component={EURecruitmentPolicyPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.HOW_TO_JOIN.JOB_DETAILS}
                element={
                  <RouteWrapper
                    component={JobDetailsPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.LIBRARY}
                element={<LibraryPage setRefs={handleSetRefs} />}
              />
              <Route
                path={ROUTES.BLOG.ROOT}
                element={<BlogPage setRefs={handleSetRefs} />}
              />
              <Route
                path={ROUTES.BLOG.POST}
                element={<BlogPostPage setRefs={handleSetRefs} />}
              />
              <Route path={ROUTES.SITEMAP} element={<SitemapPage />} />
              <Route
                path={ROUTES.LEGAL.ROOT}
                element={<LegalPage setRefs={handleSetRefs} />}
              />
              <Route
                path={ROUTES.LEGAL.COOKIE_POLICY}
                element={
                  <RouteWrapper
                    component={CookiePolicyPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.LEGAL.IMPORTANT_DISCLOSURES}
                element={
                  <RouteWrapper
                    component={ImportantDisclosuresPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.LEGAL.TAX_STRATEGY}
                element={
                  <RouteWrapper
                    component={TaxStrategyPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.LEGAL.MODERN_SLAVERY}
                element={
                  <RouteWrapper
                    component={ModernSlaveryPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.LEGAL.TERMS}
                element={
                  <RouteWrapper
                    component={TermsAndConditionsPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.LEGAL.RECRUITMENT_POLICY}
                element={
                  <RouteWrapper
                    component={RecruitmentPolicyPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              <Route
                path={ROUTES.LEGAL.PRIVACY}
                element={
                  <RouteWrapper
                    component={OnlinePrivacyStatementPage}
                    setRefs={handleSetRefs}
                  />
                }
              />
              {/* Catch-all route for 404 */}
              <Route path='*' element={<NotFoundPage />} />
            </Routes>
          </Layout>
        )}

        <CookieConsent />
      </Box>
    </>
  );
};

/**
 * Main application component with error boundary and providers
 * @returns {React.Element} Application root component
 */
const App = () => (
  <HelmetProvider>
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <RiskGate>
          <NavigationProvider>
            <AppRoutes />
          </NavigationProvider>
        </RiskGate>
      </Router>
    </ErrorBoundary>
  </HelmetProvider>
);

export default App;
